import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Label,
  Textarea,
  Input,
  Rating,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn
} from '@bliks-io/ui';
import {
  type SubmitSurveyRequest,
  type MultiFrequencyValue,
  isMultiFrequency,
  MULTI_FREQUENCY_DEFAULT_COUNT
} from '@bliks-io/contracts';
import { sanitizeDurationInput } from './surveyValidation';

/**
 * i18n key for the "how often per period?" follow-up, per "mehrmals" option.
 */
const MULTI_FREQUENCY_QUESTION_KEY: Record<MultiFrequencyValue, string> = {
  'multiple-times-daily': 'surveyDialog.timesPerDayQuestion',
  'several-times-a-week': 'surveyDialog.timesPerWeekQuestion'
};

interface RatingSectionProps {
  response: SubmitSurveyRequest;
  onResponseChange: (response: SubmitSurveyRequest) => void;
  validation: Record<string, string>;
  submitted: boolean;
  t: (key: string) => string;
}

export function RatingSection({
  response,
  onResponseChange,
  validation,
  submitted,
  t
}: RatingSectionProps) {
  const [showComment, setShowComment] = useState(false);

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">{t('surveyDialog.ratingSection')}</h3>
      <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
        <div>
          <Label className="text-base font-semibold">
            {t('surveyDialog.ratingQuestion')}
            <span className="text-danger">*</span>
          </Label>
          <div className="flex items-center gap-4 mt-2">
            <Rating
              value={response.rating?.stars || 0}
              onChange={(stars) =>
                onResponseChange({
                  ...response,
                  rating: { ...response.rating, stars, comment: response.rating?.comment || '' }
                })
              }
            />
            {submitted && validation.stars && (
              <span className="text-sm text-danger">{validation.stars}</span>
            )}
          </div>
        </div>

        <div>
          <button
            type="button"
            onClick={() => setShowComment(!showComment)}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', showComment && 'rotate-180')}
            />
            {t('surveyDialog.addCommentOptional')}
          </button>
          {showComment && (
            <Textarea
              id="comment"
              value={response.rating?.comment || ''}
              onChange={(e) =>
                onResponseChange({
                  ...response,
                  rating: {
                    ...response.rating,
                    stars: response.rating?.stars || 0,
                    comment: e.target.value
                  }
                })
              }
              placeholder={t('surveyDialog.commentPlaceholder')}
              className="mt-2"
              rows={3}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface FrequencySectionProps {
  response: SubmitSurveyRequest;
  onResponseChange: (response: SubmitSurveyRequest) => void;
  frequencyOptions: Array<{ value: string; label: string }>;
  validation: Record<string, string>;
  submitted: boolean;
  t: (key: string) => string;
}

export function FrequencySection({
  response,
  onResponseChange,
  frequencyOptions,
  validation,
  submitted,
  t
}: FrequencySectionProps) {
  const [showComment, setShowComment] = useState(false);

  const frequencyValue = response.frequency?.value || '';
  // Narrowed inside the conditional so indexing the question-key map is type-safe.
  const countQuestionKey = isMultiFrequency(frequencyValue)
    ? MULTI_FREQUENCY_QUESTION_KEY[frequencyValue]
    : null;

  return (
    <>
      <div>
        <Label htmlFor="frequency" className="text-base font-semibold">
          {t('surveyDialog.frequencyQuestion')}
          <span className="text-danger">*</span>
        </Label>
        <Select
          value={frequencyValue}
          onValueChange={(value) =>
            onResponseChange({
              ...response,
              frequency: {
                value,
                comment: response.frequency?.comment || '',
                // Prefill the per-period count on a "mehrmals" option; drop a
                // stale count when switching to a single-occurrence option.
                timesPerPeriod: isMultiFrequency(value)
                  ? MULTI_FREQUENCY_DEFAULT_COUNT[value]
                  : undefined
              }
            })
          }
        >
          <SelectTrigger className="mt-2">
            <SelectValue placeholder={t('surveyDialog.frequencyPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {frequencyOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {submitted && validation.frequencyValue && (
          <span className="text-sm text-danger">{validation.frequencyValue}</span>
        )}
      </div>

      {countQuestionKey && (
        <div>
          <Label htmlFor="frequencyTimesPerPeriod" className="text-base font-semibold">
            {t(countQuestionKey)}
            <span className="text-danger">*</span>
          </Label>
          <Input
            id="frequencyTimesPerPeriod"
            type="text"
            inputMode="numeric"
            value={response.frequency?.timesPerPeriod?.toString() ?? ''}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, '');
              const parsed = parseInt(digits, 10);
              onResponseChange({
                ...response,
                frequency: {
                  value: response.frequency?.value || '',
                  comment: response.frequency?.comment || '',
                  timesPerPeriod: isNaN(parsed) ? undefined : parsed
                }
              });
            }}
            placeholder={t('surveyDialog.timesPerPeriodPlaceholder')}
            className="mt-2"
          />
          {submitted && validation.frequencyTimesPerPeriod && (
            <span className="text-sm text-danger">{validation.frequencyTimesPerPeriod}</span>
          )}
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => setShowComment(!showComment)}
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          <ChevronDown
            className={cn('h-4 w-4 transition-transform', showComment && 'rotate-180')}
          />
          {t('surveyDialog.addCommentOptional')}
        </button>
        {showComment && (
          <Textarea
            id="frequencyComment"
            value={response.frequency?.comment || ''}
            onChange={(e) =>
              onResponseChange({
                ...response,
                frequency: {
                  value: response.frequency?.value || '',
                  comment: e.target.value,
                  // Preserve a stored count when only the comment changes.
                  timesPerPeriod: response.frequency?.timesPerPeriod
                }
              })
            }
            placeholder={t('surveyDialog.commentPlaceholder')}
            className="mt-2"
            rows={2}
          />
        )}
      </div>
    </>
  );
}

interface DurationSectionProps {
  response: SubmitSurveyRequest;
  onResponseChange: (response: SubmitSurveyRequest) => void;
  validation: Record<string, string>;
  submitted: boolean;
  t: (key: string) => string;
}

export function DurationSection({
  response,
  onResponseChange,
  validation,
  submitted,
  t
}: DurationSectionProps) {
  const [showComment, setShowComment] = useState(false);
  const [inputValue, setInputValue] = useState(response.duration?.value?.toString() || '');

  const handleDurationChange = (rawValue: string) => {
    const value = sanitizeDurationInput(rawValue);
    setInputValue(value);
    // Replace comma with dot for parsing, allow both as decimal separator
    const normalizedValue = value.replace(',', '.');
    const parsedValue = parseFloat(normalizedValue);

    onResponseChange({
      ...response,
      duration: {
        value: isNaN(parsedValue) ? 0 : parsedValue,
        comment: response.duration?.comment || ''
      }
    });
  };

  return (
    <>
      <div>
        <Label htmlFor="duration" className="text-base font-semibold">
          {t('surveyDialog.durationQuestion')}
          <span className="text-danger">*</span>
        </Label>
        <Input
          id="duration"
          type="text"
          inputMode="decimal"
          value={inputValue}
          onChange={(e) => handleDurationChange(e.target.value)}
          placeholder={t('surveyDialog.durationPlaceholder')}
          className="mt-2"
        />
        {submitted && validation.durationValue && (
          <span className="text-sm text-danger">{validation.durationValue}</span>
        )}
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowComment(!showComment)}
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          <ChevronDown
            className={cn('h-4 w-4 transition-transform', showComment && 'rotate-180')}
          />
          {t('surveyDialog.addCommentOptional')}
        </button>
        {showComment && (
          <Textarea
            id="durationComment"
            value={response.duration?.comment || ''}
            onChange={(e) =>
              onResponseChange({
                ...response,
                duration: {
                  value: response.duration?.value || 0,
                  comment: e.target.value
                }
              })
            }
            placeholder={t('surveyDialog.commentPlaceholder')}
            className="mt-2"
            rows={2}
          />
        )}
      </div>
    </>
  );
}

interface SurveyQuestionsPageProps {
  response: SubmitSurveyRequest;
  onResponseChange: (response: SubmitSurveyRequest) => void;
  frequencyOptions: Array<{ value: string; label: string }>;
  validation: Record<string, string>;
  submitted: boolean;
  enableRatingAndComment: boolean;
  enableFrequencyMeasurement: boolean;
  enableDurationMeasurement: boolean;
  t: (key: string) => string;
}

/** Second page of the survey: the classic questions the tenant enabled. */
export function SurveyQuestionsPage({
  response,
  onResponseChange,
  frequencyOptions,
  validation,
  submitted,
  enableRatingAndComment,
  enableFrequencyMeasurement,
  enableDurationMeasurement,
  t
}: SurveyQuestionsPageProps) {
  const sectionProps = { response, onResponseChange, validation, submitted, t };

  return (
    <div className="space-y-6">
      {enableRatingAndComment && <RatingSection {...sectionProps} />}

      {(enableFrequencyMeasurement || enableDurationMeasurement) && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium">{t('surveyDialog.metricsSection')}</h3>
          <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
            {enableFrequencyMeasurement && (
              <FrequencySection {...sectionProps} frequencyOptions={frequencyOptions} />
            )}
            {enableDurationMeasurement && <DurationSection {...sectionProps} />}
          </div>
        </div>
      )}
    </div>
  );
}
